/**
 * Section-jump logic behind `PanelSubTabBarV2026`, shared by the four v2026 panels.
 *
 * Host panel supplies:
 *   subTabScrollRef: 'subTabScroll'      // ref on its own .v2026-*-scroll div
 *   subTabs: { 2: [{ key, label, refs: [...] }] }   // keyed by activeTab
 * and must already expose `activeTab` and `selectedConversation`.
 *
 * Presence is measured from the DOM, so a sub-tab can never scroll nowhere. Nothing here writes
 * `activeTab` or emits `setTab`, so the strip can never bypass a disabled tab.
 */

// Gap between the strip's bottom border and the target heading.
export const SUBTAB_SCROLL_PAD = 12
// A target under this snaps to 0, so the first sub-tab keeps the pane's padding-top.
export const SUBTAB_SNAP_TOP = 16
// Spy line, measured down from the top of the scroll viewport.
export const SUBTAB_SPY_OFFSET = 24
// Lock released this long after the last scroll event. Not `scrollend` — Safari support.
export const SUBTAB_SPY_LOCK_MS = 160
// Below this the pane cannot scroll; without the gate the bottom clamp is true AT REST and a short
// pane boots with the last sub-tab lit.
export const SUBTAB_NO_SCROLL_EPS = 8

export default {
  data () {
    return {
      // { refName: Boolean } — $refs is not reactive, so a computed cannot read it directly.
      subTabPresent: {},
      activeSubKey: ''
    }
  },
  computed: {
    subTabItems () {
      return (this.subTabs && this.subTabs[this.activeTab]) || []
    },
    visibleSubTabs () {
      return this.subTabItems.filter(item => item.refs.some(name => this.subTabPresent[name]))
    },
    showSubTabBar () {
      return this.visibleSubTabs.length >= 2
    }
  },
  watch: {
    activeTab () {
      this.refreshSubTabs()
    },
    // Panels are patched, not remounted, across conversations. Only the chip is re-derived — we do
    // NOT touch scrollTop.
    selectedConversation () {
      this.refreshSubTabs()
    }
  },
  created () {
    // Outside data() on purpose: a scroll frame must not be able to trigger a re-render.
    this.subTabRafId = null
    this.subTabScrollBound = null
    this.subTabSpyLockUntil = 0
    this.subTabPresenceKey = ''
  },
  mounted () {
    this.refreshSubTabs()
  },
  updated () {
    // Guarded, or this is an infinite updated -> setState -> updated loop.
    if (this.scanSubTabPresence()) {
      this.syncActiveSubKey()
    }
  },
  beforeDestroy () {
    this.unbindSubTabScroll()
  },
  methods: {
    // A parent v-if leaves a comment node (nodeType 8) — that is the absence signal. isConnected
    // guards Vue 2's stale-$refs wart.
    resolveSubTabEl (refName) {
      const r = this.$refs[refName]
      const el = r && r.$el ? r.$el : r
      return (el && el.nodeType === 1 && el.isConnected) ? el : null
    },
    subTabScrollEl () {
      return this.resolveSubTabEl(this.subTabScrollRef)
    },
    subTabAnchorEl (item) {
      for (const name of item.refs) {
        const el = this.resolveSubTabEl(name)
        if (el) return el
      }
      return null
    },
    // Returns true when the presence set changed.
    scanSubTabPresence () {
      const next = {}
      let key = ''
      for (const item of this.subTabItems) {
        for (const name of item.refs) {
          const present = !!this.resolveSubTabEl(name)
          next[name] = present
          key += name + (present ? '1' : '0') + '|'
        }
      }
      if (key === this.subTabPresenceKey) return false
      this.subTabPresenceKey = key
      this.subTabPresent = next
      return true
    },
    // Falls back only when the active key vanished, never a blind reset to the first.
    syncActiveSubKey () {
      const items = this.visibleSubTabs
      if (items.length < 2) return
      if (!items.some(item => item.key === this.activeSubKey)) {
        this.activeSubKey = items[0].key
      }
      this.recomputeActiveSubKey(true)
    },
    refreshSubTabs () {
      this.$nextTick(() => {
        if (this._isDestroyed) return
        this.cancelSubTabFrame()
        this.bindSubTabScroll()
        this.scanSubTabPresence()
        this.syncActiveSubKey()
      })
    },
    bindSubTabScroll () {
      const el = this.subTabScrollEl()
      if (el === this.subTabScrollBound) return
      this.unbindSubTabScroll()
      if (!el) return
      el.addEventListener('scroll', this.onSubTabScroll, { passive: true })
      this.subTabScrollBound = el
    },
    unbindSubTabScroll () {
      if (this.subTabScrollBound) {
        this.subTabScrollBound.removeEventListener('scroll', this.onSubTabScroll)
        this.subTabScrollBound = null
      }
      this.cancelSubTabFrame()
    },
    cancelSubTabFrame () {
      if (this.subTabRafId) {
        cancelAnimationFrame(this.subTabRafId)
        this.subTabRafId = null
      }
    },
    onSubTabScroll () {
      // Our own smooth scroll fires a stream of events; without the lock the chip strobes through
      // every section on the way to the target.
      if (this.subTabSpyLockUntil > Date.now()) {
        this.subTabSpyLockUntil = Date.now() + SUBTAB_SPY_LOCK_MS
        return
      }
      if (this.subTabRafId) return
      this.subTabRafId = requestAnimationFrame(() => {
        this.subTabRafId = null
        this.recomputeActiveSubKey()
      })
    },
    setActiveSubKey (key) {
      if (this.activeSubKey !== key) this.activeSubKey = key
    },
    recomputeActiveSubKey (force) {
      if (!force && this.subTabSpyLockUntil > Date.now()) return
      const items = this.visibleSubTabs
      if (items.length < 2) return
      const scrollEl = this.subTabScrollEl()
      if (!scrollEl) return

      // Nothing to scroll: the top section is what the user is looking at.
      if ((scrollEl.scrollHeight - scrollEl.clientHeight) <= SUBTAB_NO_SCROLL_EPS) {
        this.setActiveSubKey(items[0].key)
        return
      }
      // Otherwise a short final section could never become active.
      if (scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 2) {
        this.setActiveSubKey(items[items.length - 1].key)
        return
      }

      const line = scrollEl.getBoundingClientRect().top + SUBTAB_SPY_OFFSET
      let picked = items[0].key
      for (const item of items) {
        const el = this.subTabAnchorEl(item)
        if (el && el.getBoundingClientRect().top <= line) picked = item.key
      }
      this.setActiveSubKey(picked)
    },
    subTabReducedMotion () {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    },
    goToSubTab (key) {
      const item = this.visibleSubTabs.find(entry => entry.key === key)
      if (!item) return
      const scrollEl = this.subTabScrollEl()
      if (!scrollEl) return
      // Re-resolved at click time: a stale anchor makes the jump a silent no-op.
      const el = this.subTabAnchorEl(item)
      if (!el) return

      this.setActiveSubKey(key)
      this.subTabSpyLockUntil = Date.now() + SUBTAB_SPY_LOCK_MS

      let top = el.getBoundingClientRect().top -
                scrollEl.getBoundingClientRect().top +
                scrollEl.scrollTop -
                SUBTAB_SCROLL_PAD
      if (top < SUBTAB_SNAP_TOP) top = 0
      // Never scrollIntoView: it walks every scrollable ancestor and would move the chat pane.
      scrollEl.scrollTo({
        top: Math.max(0, top),
        behavior: this.subTabReducedMotion() ? 'auto' : 'smooth'
      })
    }
  }
}
